<template>
    <InfiniteScroller #default="{ listPortion }" :list="listFiltered">
        <div v-if="isFetching" class="l-padded">
            <VSpinner size="medium" :line-fg-color="spinnerColor" :speed="1" />
        </div>

        <div
            v-else-if="!listFiltered.length"
            class="l-padded l-inline l-center t-subtle t-small"
        >
            <span>
                {{ $t('shared.noSearchResult') }}
            </span>
        </div>

        <table v-else>
            <thead>
                <tr>
                    <th>
                        {{ $t('name') }}

                        <IconButton @click="handleSort('name')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'name' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('createdBy') }}

                        <IconButton @click="handleSort('created_by')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'created_by' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('createdAt') }}

                        <IconButton @click="handleSort('created')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'created' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('state') }}
                    </th>

                    <th>
                        {{ $t('data') }}
                    </th>

                    <th>
                        {{ $t('template') }}
                    </th>

                    <th>
                        {{ $t('actions') }}
                    </th>
                </tr>
            </thead>

            <template v-for="item in listPortion">
                <tr :key="item.id">
                    <td>
                        <div class="report-name-cell">
                            <span>{{ item.name }}</span>

                            <a
                                v-if="item.attachment_url"
                                :href="item.attachment_url"
                                download
                                :title="
                                    $t('downloadAttachmentActionTitle', [
                                        item.name,
                                    ])
                                "
                            >
                                <IconButton
                                    class="action-chip action-chip--static"
                                >
                                    <AttachmentIcon />
                                </IconButton>
                            </a>
                        </div>
                    </td>

                    <td>
                        {{ item.created_by }}
                    </td>

                    <td>
                        {{ item.createdFormatted }}
                    </td>

                    <td>
                        <div class="l-inline l-center-v l-gap-1">
                            <StateTag
                                :variant="getStateVariant(item.state)"
                                :label="getStateLabel(item.state)"
                                :title="getStateDescription(item.state)"
                                :icon="getStateIcon(item.state)"
                                :icon-size="getStateIconSize(item.state)"
                            />

                            <span
                                v-if="item.has_error"
                                class="state-error-icon"
                                :title="$t('generationErrorDescription')"
                            >
                                <WarningOutlinedIcon
                                    width="18"
                                    height="18"
                                    color="currentColor"
                                />
                            </span>
                        </div>
                    </td>

                    <td>
                        <a
                            v-if="item.data_url"
                            :href="item.data_url"
                            download
                            :title="$t('downloadDataActionTitle', [item.name])"
                        >
                            <IconButton class="action-chip action-chip--static">
                                <DownloadIcon />
                            </IconButton>
                        </a>
                    </td>

                    <td>
                        <div
                            v-if="item.rendered_template_url"
                            class="l-inline l-gap-1"
                        >
                            <a
                                :href="item.rendered_template_url"
                                target="_blank"
                                :title="
                                    $t('showReportActionTitle', [item.name])
                                "
                            >
                                <IconButton
                                    class="action-chip action-chip--neutral"
                                >
                                    <SearchIcon />
                                </IconButton>
                            </a>

                            <a
                                :href="item.rendered_template_url"
                                target="_blank"
                                download
                                :title="
                                    $t('downloadReportActionTitle', [item.name])
                                "
                            >
                                <IconButton
                                    class="action-chip action-chip--static"
                                >
                                    <DownloadIcon />
                                </IconButton>
                            </a>
                        </div>
                    </td>

                    <td>
                        <IconButton
                            class="action-chip action-chip--danger"
                            :title="$t('deleteReportActionTitle', [item.name])"
                            @click="handleDelete(item.id)"
                        >
                            <TrashBinIcon />
                        </IconButton>
                    </td>
                </tr>
            </template>
        </table>
    </InfiniteScroller>
</template>

<script>
import moment from 'moment-timezone'
import VSpinner from 'vue-simple-spinner'

import { httpHelper } from '@/utils'
import AttachmentIcon from '@/components/icons/AttachmentIcon'
import CheckCircleIcon from '@/components/icons/CheckCircleIcon'
import ClockIcon from '@/components/icons/ClockIcon'
import ControlsPlayIcon from '@/components/icons/ControlsPlayIcon'
import DownloadIcon from '@/components/icons/DownloadIcon'
import IconButton from '@/components/IconButton'
import InfiniteScroller from '@/components/InfiniteScroller'
import SearchIcon from '@/components/icons/SearchIcon'
import SortArrowIcon from '@/components/icons/SortArrowIcon'
import StateTag from '@/components/StateTag'
import TrashBinIcon from '@/components/icons/TrashBinIcon'
import WarningOutlinedIcon from '@/components/icons/WarningOutlinedIcon'

const defaultSortColumn = 'created'
const defaultSortDirection = 1
const defaultReportState = 'pending'
const reportStateMeta = {
    pending: {
        icon: ClockIcon,
        iconSize: 18,
        variant: 'warning',
    },
    running: {
        icon: ControlsPlayIcon,
        iconSize: 16,
        variant: 'info',
    },
    done: {
        icon: CheckCircleIcon,
        iconSize: 16,
        variant: 'success',
    },
}

export default {
    name: 'ReportingHistoryTable',
    components: {
        AttachmentIcon,
        DownloadIcon,
        IconButton,
        InfiniteScroller,
        SearchIcon,
        SortArrowIcon,
        StateTag,
        TrashBinIcon,
        VSpinner,
        WarningOutlinedIcon,
    },
    props: {
        filterName: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isFetching: false,
            list: [],
            sortBy: defaultSortColumn,
            sortDirection: defaultSortDirection,
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
        }
    },
    computed: {
        listFiltered() {
            let result = this.list

            if (this.filterName) {
                const query = this.filterName.toLowerCase()
                result = result.filter(item =>
                    item.name.toLowerCase().includes(query)
                )
            }

            if (this.sortBy) {
                result = [...result].sort((a, b) => {
                    const aValue = a[this.sortBy]
                    const bValue = b[this.sortBy]

                    if (this.sortBy === 'created') {
                        return (
                            this.sortDirection *
                            (new Date(bValue).getTime() -
                                new Date(aValue).getTime())
                        )
                    } else if (aValue === bValue) {
                        return 0
                    } else if (aValue == null) {
                        return 1
                    } else if (bValue == null) {
                        return -1
                    } else if (typeof aValue === 'string') {
                        return aValue.localeCompare(bValue) * this.sortDirection
                    }

                    return aValue > bValue
                        ? this.sortDirection
                        : -this.sortDirection
                })
            }

            return result
        },
    },
    mounted() {
        this.fetchReportingHistory()
    },
    methods: {
        async fetchReportingHistory() {
            this.isFetching = true
            const { data } = await httpHelper.get('/reports/')
            this.list = data.results.map(item => ({
                ...item,
                createdFormatted: this.formatTimestamp(item.created),
            }))
            this.isFetching = false
        },
        formatTimestamp(timestamp) {
            return moment(timestamp).format('DD.MM.YYYY HH:mm')
        },
        normalizeState(state) {
            return reportStateMeta[state] ? state : defaultReportState
        },
        getStateMeta(state) {
            return reportStateMeta[this.normalizeState(state)]
        },
        getStateLabel(state) {
            const stateKey = this.normalizeState(state)
            const labelKey = `stateValues.${stateKey}`
            return this.$te(labelKey) ? this.$t(labelKey) : stateKey
        },
        getStateDescription(state) {
            const stateKey = this.normalizeState(state)
            const descriptionKey = `stateDescriptions.${stateKey}`
            return this.$te(descriptionKey)
                ? this.$t(descriptionKey)
                : this.getStateLabel(stateKey)
        },
        getStateIcon(state) {
            return this.getStateMeta(state).icon
        },
        getStateIconSize(state) {
            return this.getStateMeta(state).iconSize
        },
        getStateVariant(state) {
            return this.getStateMeta(state).variant
        },
        async handleDelete(reportId) {
            this.isFetching = true
            await httpHelper.drop(`/reports/${reportId}/`)
            await this.fetchReportingHistory()
        },
        handleSort(column) {
            if (this.sortBy !== column || this.sortDirection !== 1) {
                this.sortBy = column
                this.sortDirection = 1
            } else if (this.sortDirection === 1) {
                this.sortDirection = -1
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "actions": "Actions",
        "createdAt": "Created at",
        "createdBy": "Created by",
        "data": "Data",
        "deleteReportActionTitle": "Delete report {0}",
        "download": "Download",
        "downloadAttachmentActionTitle": "Download attachment for report {0}",
        "downloadDataActionTitle": "Download data for report {0}",
        "downloadReportActionTitle": "Download report {0}",
        "generationErrorDescription": "There was an issue creating the report. Please try again or contact our support team.",
        "name": "Name",
        "state": "State",
        "stateDescriptions": {
            "done": "Report creation has finished.",
            "pending": "The report is planned and waiting to be generated.",
            "running": "The report is currently being generated."
        },
        "stateValues": {
            "done": "Done",
            "pending": "Pending",
            "running": "Running"
        },
        "show": "Show",
        "showReportActionTitle": "Open report {0}",
        "template": "Report"
    },
    "de": {
        "actions": "Aktionen",
        "createdAt": "Erstellt am",
        "createdBy": "Erstellt von",
        "data": "Daten",
        "deleteReportActionTitle": "Report {0} löschen",
        "download": "Herunterladen",
        "downloadAttachmentActionTitle": "Anhang für Report {0} herunterladen",
        "downloadDataActionTitle": "Daten für Report {0} herunterladen",
        "downloadReportActionTitle": "Report {0} herunterladen",
        "generationErrorDescription": "Bei der Erstellung des Reports ist ein Problem aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie unser Support-Team.",
        "name": "Name",
        "state": "Status",
        "stateDescriptions": {
            "done": "Die Erstellung des Reports ist abgeschlossen.",
            "pending": "Der Report ist geplant und wartet auf die Erstellung.",
            "running": "Der Report wird aktuell erstellt."
        },
        "stateValues": {
            "done": "Abgeschlossen",
            "pending": "Ausstehend",
            "running": "Laufend"
        },
        "show": "Anzeigen",
        "showReportActionTitle": "Report {0} öffnen",
        "template": "Report"
    },
    "fr": {
        "actions": "Actions",
        "createdAt": "Créé à",
        "createdBy": "Créé par",
        "data": "Data",
        "deleteReportActionTitle": "Supprimer le rapport {0}",
        "download": "Télécharger",
        "downloadAttachmentActionTitle": "Télécharger la pièce jointe du rapport {0}",
        "downloadDataActionTitle": "Télécharger les données du rapport {0}",
        "downloadReportActionTitle": "Télécharger le rapport {0}",
        "generationErrorDescription": "Un problème est survenu lors de la création du rapport. Veuillez réessayer ou contacter notre équipe de support.",
        "name": "Nom",
        "state": "État",
        "stateDescriptions": {
            "done": "La création du rapport est terminée.",
            "pending": "Le rapport est planifié et en attente de génération.",
            "running": "Le rapport est en cours de génération."
        },
        "stateValues": {
            "done": "Terminé",
            "pending": "En attente",
            "running": "En cours"
        },
        "show": "Afficher",
        "showReportActionTitle": "Ouvrir le rapport {0}",
        "template": "Rapport"
    },
    "it": {
        "actions": "Azione",
        "createdAt": "Creato a",
        "createdBy": "Creato da",
        "data": "Dati",
        "deleteReportActionTitle": "Elimina rapporto {0}",
        "download": "Scaricare",
        "downloadAttachmentActionTitle": "Scarica l'allegato per il rapporto {0}",
        "downloadDataActionTitle": "Scarica i dati per il rapporto {0}",
        "downloadReportActionTitle": "Scarica il rapporto {0}",
        "generationErrorDescription": "Si è verificato un problema durante la creazione del rapporto. Riprova o contatta il nostro team di supporto.",
        "name": "Nome",
        "state": "Stato",
        "stateDescriptions": {
            "done": "La creazione del rapporto è completata.",
            "pending": "Il rapporto è pianificato ed è in attesa di essere generato.",
            "running": "Il rapporto è attualmente in fase di creazione."
        },
        "stateValues": {
            "done": "Completato",
            "pending": "In attesa",
            "running": "In esecuzione"
        },
        "show": "Mostra",
        "showReportActionTitle": "Apri rapporto {0}",
        "template": "Rapporto"
    }
}
</i18n>

<style lang="scss" scoped>
table {
    display: block;
    width: 100%;
    border-spacing: 0;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.7);

    thead {
        position: sticky;
        top: 0;
        background-color: #fff;
        z-index: 1;

        .icon-button {
            margin-left: 5px;
        }
    }

    tr {
        &:not(:first-of-type) {
            td {
                border-top: $style-border;
            }
        }

        th {
            text-align: left;
            border-bottom: $style-border;
        }

        th,
        td {
            padding: 1rem;
            white-space: nowrap;

            &:first-child {
                padding-left: 2rem;
            }

            &:last-child {
                padding-right: 2rem;
            }

            &:not(:last-child) {
                width: 1%;
            }
        }
    }

    a {
        display: block;

        .icon-button {
            display: block;
        }
    }

    .report-name-cell {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;

        a {
            display: inline-flex;
        }
    }

    .state-error-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: $color-red;
    }
}
</style>
